import { AiChatState } from "@intelligems/ig-types/schemas/ai/ai-chat-state";

/** Cap so a long creation chat can't blow up Viv's first-turn token budget. */
const MAX_BRIEF_CHARS = 6000;

/**
 * Flattens an experience-creation chat transcript into a plain-text brief for
 * Viv's first turn — the merchant's established intent and the visual changes
 * they asked for, so the visual-builder agent opens warm instead of cold.
 *
 * This is the MVP "raw transcript" generation: it distills the conversation
 * into `User:` / `Assistant:` lines rather than summarizing with an LLM. The
 * consumer contract (`variables.creationBrief`) is identical either way, so
 * swapping in a dedicated summarizer later is a change confined to this file.
 *
 * Returns undefined when there is nothing worth handing off (no session or no
 * text messages), so the caller can skip the stash entirely.
 */
export const buildCreationBrief = (aiChat: AiChatState): string | undefined => {
  if (!aiChat.sessionId || aiChat.turns.length === 0) {
    return undefined;
  }

  const lines: string[] = [];
  for (const turn of aiChat.turns) {
    for (const node of turn.nodes) {
      for (const message of node.messages) {
        if (message.role !== "user" && message.role !== "assistant") {
          continue;
        }
        const text = message.parts
          .flatMap((part) =>
            part.type === "text" && part.text ? [part.text] : []
          )
          .join("")
          .trim();
        if (!text) {
          continue;
        }
        lines.push(
          `${message.role === "user" ? "User" : "Assistant"}: ${text}`
        );
      }
    }
  }

  if (lines.length === 0) {
    return undefined;
  }

  const brief = lines.join("\n");
  // Keep the tail: the most recent exchanges carry the freshest intent.
  return brief.length > MAX_BRIEF_CHARS
    ? brief.slice(brief.length - MAX_BRIEF_CHARS)
    : brief;
};
